import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { nanoApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, Sparkles, Search } from 'lucide-react';
import { LoadingState, EmptyState, Pagination } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Nano, TipStatus } from '@/types/learningManagement';

export const NanoListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterDifficulty, setFilterDifficulty] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'nano');
  const canEdit = hasPermission('edit', 'nano');
  const canDelete = hasPermission('delete', 'nano');

  // Build fetch function with filters
  const fetchNanosFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return nanoApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else {
        return nanoApi.list({
          page: params.page,
          size: params.size,
          sortBy: 'createdAt',
          sortDirection: 'desc',
        });
      }
    };
  }, [searchQuery]);

  // Use paginated list hook
  const {
    data: nanos,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Nano>({
    fetchFn: fetchNanosFn,
    entityName: 'nanos',
    suppress404Errors: true,
  });

  // Apply client-side filters
  const filteredNanos = useMemo(() => {
    if (!nanos) return [];
    let filtered = nanos;
    if (filterCategory !== 'all') {
      filtered = filtered.filter((n) => n.category === filterCategory);
    }
    if (filterDifficulty !== 'all') {
      filtered = filtered.filter((n) => n.difficultyLevel === filterDifficulty);
    }
    return filtered;
  }, [nanos, filterCategory, filterDifficulty]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (nanoId: string) => {
      await nanoApi.delete(nanoId);
    },
    entityName: 'nano',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (nanoId: string) => {
    if (!window.confirm('Are you sure you want to delete this nano?')) return;
    await deleteMutation.mutate(nanoId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: TipStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
      case 'IN_PROGRESS':
      case 'NOT_STARTED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ARCHIVED':
      case 'EXPIRED':
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const nanoId = (nano: Nano) => nano.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nano Management</h1>
          <p className="text-muted-foreground mt-2">Manage all nanos</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/nanos/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Nano
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search nanos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="DAILY_TIP">Daily Tip</SelectItem>
                <SelectItem value="WEEKLY_TIP">Weekly Tip</SelectItem>
                <SelectItem value="MONTHLY_TIP">Monthly Tip</SelectItem>
                <SelectItem value="QUICK_ADVICE">Quick Advice</SelectItem>
                <SelectItem value="IN_DEPTH_GUIDE">In-Depth Guide</SelectItem>
                <SelectItem value="CHECKLIST">Checklist</SelectItem>
                <SelectItem value="BEST_PRACTICES">Best Practices</SelectItem>
                <SelectItem value="TIPS_AND_TRICKS">Tips and Tricks</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
              <SelectTrigger>
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="EASY">Easy</SelectItem>
                <SelectItem value="MODERATE">Moderate</SelectItem>
                <SelectItem value="CHALLENGING">Challenging</SelectItem>
                <SelectItem value="ADVANCED">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Nanos Table */}
      <Card>
        <CardHeader>
          <CardTitle>Nanos ({pagination.totalElements || 0})</CardTitle>
          <CardDescription>List of all nanos</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading nanos..." />
          ) : (error || (nanos && nanos.length === 0)) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load nanos. Please try again or create a new one." : "Get started by creating your first nano."}
              icon={Sparkles}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/nanos/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredNanos.map((nano) => (
                    <TableRow key={nanoId(nano)}>
                      <TableCell className="font-medium">{nano.title}</TableCell>
                      <TableCell>{nano.category}</TableCell>
                      <TableCell>{nano.tipType}</TableCell>
                      <TableCell>{nano.difficultyLevel}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(nano.status)}`}>
                          {nano.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(nano.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/nanos/${nanoId(nano)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/nanos/${nanoId(nano)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(nanoId(nano))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                size={pagination.size}
                onPageChange={setPage}
                onSizeChange={setSize}
                isLoading={isLoading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NanoListPage;
